import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft, Loader2, Mail, Calendar, MapPin, Building2, Phone, Award, Lock, Pencil,
  Palmtree, Receipt, Printer, AlertCircle, Users, GraduationCap, Languages, BadgeCheck,
  Tag, FileText, Briefcase, LifeBuoy, ListChecks, Target, TrendingUp, ChevronRight, Sparkles,
  Shirt, Gavel,
} from 'lucide-react';
import {
  atlasApi, AtlasUser, Colaborador, FeriasResumo, Holerite, Idioma, OnboardingExecucao,
  AvaliacaoCiclo, LABEL_GENERO, LABEL_PECA_UNIFORME, PecaUniforme,
} from '../shared/atlasApi';
import HistoricoDisciplinar from '../Disciplinar/HistoricoDisciplinar';
import AvatarColaborador from '../shared/AvatarColaborador';
import BadgeStatus from '../shared/BadgeStatus';
import ModalEditarFicha from './ModalEditarFicha';
import EtapasOnboarding from '../Onboarding/EtapasOnboarding';
import Resultado from '../Desempenho/Resultado';
import PdiApp from '../PDI';
import AbaDiscFicha from '../DISC/AbaDiscFicha';

interface FichaProps {
  user: AtlasUser;
  funccodi: number;
  podeEditar: boolean;
  onVoltar: () => void;
}

type Aba = 'geral' | 'ferias' | 'holerite' | 'onboarding' | 'desempenho' | 'pdi' | 'disc' | 'ocorrencias';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function formatarData(s?: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

function formatarCPF(cpf?: string): string {
  if (!cpf) return '—';
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

const LABEL_NIVEL_FORMACAO: Record<string, string> = {
  medio: 'Ensino médio', tecnico: 'Técnico', superior: 'Superior',
  pos: 'Pós-graduação', mestrado: 'Mestrado', doutorado: 'Doutorado',
};
const LABEL_SITUACAO: Record<string, string> = {
  concluido: 'Concluído', em_andamento: 'Em andamento', trancado: 'Trancado',
};
const LABEL_NIVEL_IDIOMA: Record<string, string> = {
  basico: 'Básico', intermediario: 'Intermediário', avancado: 'Avançado',
  fluente: 'Fluente', nativo: 'Nativo',
};

function cap(s: string): string { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

const SectionVazia: React.FC<{ msg: string }> = ({ msg }) => (
  <p className="text-sm text-gray-400 italic">{msg}</p>
);

const Campo: React.FC<{ icon?: React.ReactNode; label: string; valor?: React.ReactNode }> = ({ icon, label, valor }) => (
  <div className="flex items-start gap-3 py-2">
    {icon && <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5">{icon}</div>}
    <div className="min-w-0 flex-1">
      <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-sm text-gray-800 break-words">{valor ?? <span className="text-gray-300">—</span>}</div>
    </div>
  </div>
);

// ============================================================
// Aba Férias
// ============================================================
const AbaFerias: React.FC<{ user: AtlasUser; funccodi: number; mostrarValor: boolean }> = ({ user, funccodi, mostrarValor }) => {
  const [dados, setDados] = useState<FeriasResumo | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getFerias(user, funccodi)
      .then(r => { if (!cancel) setDados(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar férias'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando férias...</div>;
  if (erro) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  if (!dados) return null;

  const p = dados.periodo_aquisitivo_atual;
  const gozados = p?.dias_gozados ?? 0;
  const direito = p?.dias_direito ?? 30;
  const pct = Math.min(100, Math.round((gozados / direito) * 100));

  let tag: { classes: string; label: string } | null = null;
  if (p?.vence_em) {
    const dias = Math.ceil((new Date(p.vence_em).getTime() - Date.now()) / 86400000);
    const classes = dias < 60 ? 'bg-rose-100 text-rose-700' : dias < 180 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
    tag = { classes, label: dias >= 0 ? `Vence em ${dias} dias` : `Vencido há ${Math.abs(dias)} dias` };
  }

  return (
    <div className="space-y-4">
      {/* Resumo */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Saldo disponível</div>
            <div className="text-4xl font-black text-emerald-600">{dados.saldo_dias ?? '—'}<span className="text-base font-medium text-gray-400"> dias</span></div>
          </div>
          {tag && <span className={`px-3 py-1 rounded-full text-xs font-medium ${tag.classes}`}>{tag.label}</span>}
        </div>
        {p && (
          <>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Gozados no período atual</span>
                <span>{gozados} / {direito} dias</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">
              <Campo label="Período aquisitivo" valor={`${formatarData(p.inicio)} a ${formatarData(p.fim)}`} />
              <Campo label="Dias disponíveis" valor={`${p.dias_disponiveis} dias`} />
              <Campo label="Vencimento" valor={formatarData(p.vence_em)} />
            </div>
          </>
        )}
      </div>

      {/* Histórico (timeline) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Histórico de férias</h3>
        {dados.historico.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhum registro de férias.</p>
        ) : (
          <ul className="space-y-3">
            {dados.historico.map((h, i) => (
              <li key={i} className="flex items-start gap-3 border-l-2 border-purple-200 pl-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-800">
                    {formatarData(h.inicio_concedido || h.inicio_programado)}
                    {h.descricao && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                        {h.descricao}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {h.dias != null ? `${h.dias} dias` : '—'}
                    {h.periodo_inicio && ` · período ${formatarData(h.periodo_inicio)}–${formatarData(h.periodo_fim)}`}
                  </div>
                </div>
                {mostrarValor && h.valor != null && (
                  <div className="text-sm font-semibold text-gray-700">{BRL.format(h.valor)}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Aba Holerite
// ============================================================
const AbaHolerite: React.FC<{ user: AtlasUser; funccodi: number }> = ({ user, funccodi }) => {
  const [competencias, setCompetencias] = useState<string[]>([]);
  const [competencia, setCompetencia] = useState<string>('');
  const [holerite, setHolerite] = useState<Holerite | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    atlasApi.listarCompetenciasHolerite(user, funccodi)
      .then(r => { if (!cancel) { setCompetencias(r.competencias || []); setCompetencia((r.competencias || [])[0] || ''); } })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar competências'); });
    return () => { cancel = true; };
  }, [user, funccodi]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getHolerite(user, funccodi, competencia || undefined)
      .then(r => { if (!cancel) setHolerite(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar holerite'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi, competencia]);

  return (
    <div className="space-y-3 holerite-print">
      <style>{`@media print { body * { visibility: hidden; } .holerite-print, .holerite-print * { visibility: visible; } .holerite-print { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } }`}</style>

      <div className="flex items-center gap-3 flex-wrap no-print">
        <select
          value={competencia}
          onChange={e => setCompetencia(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          {competencias.length === 0 && <option value="">—</option>}
          {competencias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      </div>

      {erro ? (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>
      ) : loading ? (
        <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando holerite...</div>
      ) : holerite && holerite.competencia ? (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-bold text-gray-900">Holerite — competência {holerite.competencia}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Evento</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-right">Referência</th>
                <th className="px-4 py-2 text-right">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {holerite.lancamentos.map((l, i) => (
                <tr key={i} className={l.tipo === 'I' ? 'bg-gray-50/60 italic' : ''}>
                  <td className="px-4 py-2 text-gray-500">{l.evento ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-800">{l.descricao}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{l.referencia ?? '—'}</td>
                  <td className={`px-4 py-2 text-right font-medium ${l.tipo === 'D' ? 'text-rose-600' : l.tipo === 'V' ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {l.tipo === 'D' ? '- ' : ''}{BRL.format(l.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 text-sm">
              <tr>
                <td colSpan={3} className="px-4 py-2 text-right text-gray-500">Vencimentos</td>
                <td className="px-4 py-2 text-right text-emerald-600 font-semibold">{BRL.format(holerite.totais.vencimentos)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="px-4 py-2 text-right text-gray-500">Descontos</td>
                <td className="px-4 py-2 text-right text-rose-600 font-semibold">{BRL.format(holerite.totais.descontos)}</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td colSpan={3} className="px-4 py-2 text-right font-bold text-gray-900">Líquido</td>
                <td className="px-4 py-2 text-right font-bold text-gray-900">{BRL.format(holerite.totais.liquido)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400">Nenhum holerite disponível.</div>
      )}

      <p className="text-[11px] text-amber-600 flex items-center gap-1 no-print">
        <AlertCircle className="w-3.5 h-3.5" /> Este acesso é registrado para fins de auditoria LGPD.
      </p>
    </div>
  );
};

// ============================================================
// Aba Onboarding
// ============================================================
const AbaOnboarding: React.FC<{ user: AtlasUser; funccodi: number; podeEditar: boolean }> = ({ user, funccodi, podeEditar }) => {
  const [execs, setExecs] = useState<OnboardingExecucao[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getOnboardingExecucoes(user, { funccodi })
      .then(r => { if (!cancel) setExecs(r.itens || []); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar onboarding'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando onboarding...</div>;
  if (erro) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  if (execs.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">
        <ListChecks className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        Nenhum processo de onboarding para este colaborador.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {execs.map(ex => (
        <div key={ex._id} className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900">{ex.trilha_nome}</h3>
              <p className="text-xs text-gray-400">Início {formatarData(ex.data_inicio)}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
              ex.status === 'concluido' ? 'bg-emerald-100 text-emerald-700'
              : ex.status === 'cancelado' ? 'bg-gray-100 text-gray-500'
              : ex.atrasada ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'
            }`}>
              {ex.status === 'concluido' ? 'Concluído' : ex.status === 'cancelado' ? 'Cancelado' : ex.atrasada ? 'Atrasado' : 'Em andamento'}
            </span>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progresso</span>
              <span>{ex.progresso_pct}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${ex.status === 'concluido' ? 'bg-emerald-500' : ex.atrasada ? 'bg-rose-500' : 'bg-purple-500'}`} style={{ width: `${ex.progresso_pct}%` }} />
            </div>
          </div>
          <div className="mt-4">
            <EtapasOnboarding
              user={user}
              execId={ex._id}
              etapas={ex.etapas}
              podeEditar={podeEditar && ex.status === 'em_andamento'}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// Aba Desempenho — ciclos encerrados + resultado do colaborador
// ============================================================
const AbaDesempenho: React.FC<{ user: AtlasUser; funccodi: number }> = ({ user, funccodi }) => {
  const [ciclos, setCiclos] = useState<AvaliacaoCiclo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [cicloSel, setCicloSel] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getCiclosAval(user, { status: 'encerrado' })
      .then(r => { if (!cancel) setCiclos(r.itens || []); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar ciclos'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando avaliações...</div>;
  if (erro) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;

  if (cicloSel) {
    return (
      <div className="space-y-3">
        <button onClick={() => setCicloSel(null)} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Voltar aos ciclos</button>
        <Resultado user={user} cicloId={cicloSel} funccodi={funccodi} fixo />
      </div>
    );
  }

  if (ciclos.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">
        <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        Nenhum ciclo de avaliação encerrado.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Ciclos encerrados</h3>
      <ul className="space-y-2">
        {ciclos.map(c => (
          <li key={c._id}>
            <button onClick={() => setCicloSel(c._id)} className="w-full text-left bg-white rounded-2xl border border-gray-200 p-4 hover:border-purple-300 hover:shadow-sm transition flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">{c.nome}</div>
                <div className="text-xs text-gray-400">{c.data_fim ? `Encerrado · ${formatarData(c.data_fim)}` : 'Encerrado'}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ============================================================
// Ficha
// ============================================================
const FichaColaborador: React.FC<FichaProps> = ({ user, funccodi, podeEditar, onVoltar }) => {
  const [colab, setColab] = useState<Colaborador | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [aba, setAba] = useState<Aba>('geral');

  const carregar = useCallback(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.getColaborador(user, funccodi)
      .then(r => { if (!cancel) setColab(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi]);

  useEffect(() => carregar(), [carregar]);

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Carregando ficha...
      </div>
    );
  }

  if (erro || !colab) {
    return (
      <div className="p-6">
        <button onClick={onVoltar} className="text-sm text-purple-600 hover:underline flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">
          {erro || 'Colaborador não encontrado.'}
        </div>
      </div>
    );
  }

  const d = colab.dados_publicos;
  const e = colab.enriquecimento || {};
  const sensivelOculto = colab._sensiveis_ocultos;
  const podeVerSensiveis = !colab._sensiveis_ocultos;
  const isOwn = !!d.email_corporativo && !!user.email && d.email_corporativo.toLowerCase() === user.email.toLowerCase();

  const abas: { id: Aba; label: string; icon: React.ReactNode }[] = [
    { id: 'geral', label: 'Geral', icon: null },
    { id: 'ferias', label: 'Férias', icon: <Palmtree className="w-4 h-4" /> },
    { id: 'onboarding', label: 'Onboarding', icon: <ListChecks className="w-4 h-4" /> },
    { id: 'desempenho', label: 'Desempenho', icon: <Target className="w-4 h-4" /> },
    { id: 'pdi', label: 'PDI', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'disc', label: 'DISC', icon: <Sparkles className="w-4 h-4" /> },
    { id: 'ocorrencias', label: 'Ocorrências', icon: <Gavel className="w-4 h-4" /> },
    ...(podeVerSensiveis ? [{ id: 'holerite' as Aba, label: 'Holerite', icon: <Receipt className="w-4 h-4" /> }] : []),
  ];

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Voltar para a lista
      </button>

      {/* Cabeçalho */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center gap-5">
        <AvatarColaborador
          nome={d.nome}
          fotoUrl={(d.tem_foto_gix || e.foto_url) ? atlasApi.fotoUrl(user, colab.funccodi, colab.data_sincronizacao) : undefined}
          size={72}
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold text-gray-900 truncate">{d.nome || '—'}</h2>
          <p className="text-sm text-gray-500 truncate">
            {d.cargo_nome || 'Sem cargo'} {d.centro_custo_nome && `· ${d.centro_custo_nome}`}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <BadgeStatus status={d.status} />
            <span className="text-xs text-gray-400">Mat. {d.matricula || colab.funccodi}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
              origem: {colab.origem}
            </span>
          </div>
        </div>
        {podeEditar && (
          <button
            onClick={() => setModalAberto(true)}
            className="ml-auto self-start px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 transition"
          >
            <Pencil className="w-4 h-4" /> Editar ficha
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {abas.map(a => (
          <button
            key={a.id}
            onClick={() => setAba(a.id)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition ${
              aba === a.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {a.icon}{a.label}
          </button>
        ))}
      </div>

      {aba === 'geral' && (
        <>
          {/* Dados profissionais */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Dados profissionais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Campo icon={<Calendar className="w-4 h-4" />} label="Admissão" valor={formatarData(d.data_admissao)} />
              <Campo icon={<Calendar className="w-4 h-4" />} label="Demissão" valor={d.data_demissao ? formatarData(d.data_demissao) : <span className="text-emerald-600">Ainda ativo</span>} />
              <Campo icon={<Building2 className="w-4 h-4" />} label="Centro de custo" valor={d.centro_custo_nome} />
              <Campo icon={<Award className="w-4 h-4" />} label="Cargo" valor={d.cargo_nome} />
              <Campo icon={<Mail className="w-4 h-4" />} label="E-mail corporativo" valor={d.email_corporativo} />
              <Campo label="Departamento (cód)" valor={d.departamento_codigo} />
            </div>
          </section>

          {/* Dados pessoais */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Dados pessoais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Campo icon={<Calendar className="w-4 h-4" />} label="Data de nascimento" valor={formatarData(d.data_nascimento)} />
              <Campo label="Gênero" valor={e.genero ? LABEL_GENERO[e.genero] : undefined} />
              {e.nome_social && <Campo label="Nome social" valor={e.nome_social} />}
              <Campo label="Nome da mãe" valor={d.nome_mae} />
              <Campo label="Nome do pai" valor={d.nome_pai} />
              <Campo label="RG" valor={d.rg} />
              <Campo icon={<MapPin className="w-4 h-4" />} label="Endereço (GIX)" valor={d.endereco} />
            </div>
          </section>

          {/* Enriquecimento Mongo */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Contatos & Observações</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Campo icon={<Phone className="w-4 h-4" />} label="Celular" valor={e.celular} />
              <Campo icon={<Phone className="w-4 h-4" />} label="Telefone pessoal" valor={e.telefone_pessoal} />
              <Campo icon={<Mail className="w-4 h-4" />} label="E-mail pessoal" valor={e.email_pessoal} />
              <Campo label="Contato emergência" valor={e.contato_emergencia} />
              <Campo label="Competências" valor={e.competencias} />
              <Campo label="Tamanho de camisa" valor={e.tamanho_camisa} />
            </div>
            {e.observacoes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                {e.observacoes}
              </div>
            )}
          </section>

          {/* Endereço */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" /> Endereço
            </h3>
            {e.endereco && Object.values(e.endereco).some(v => v) ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <Campo label="CEP" valor={e.endereco.cep} />
                  <Campo label="Logradouro" valor={[e.endereco.logradouro, e.endereco.numero].filter(Boolean).join(', ')} />
                  <Campo label="Complemento" valor={e.endereco.complemento} />
                  <Campo label="Bairro" valor={e.endereco.bairro} />
                  <Campo label="Cidade / UF" valor={[e.endereco.cidade, e.endereco.uf].filter(Boolean).join(' / ')} />
                </div>
              </>
            ) : (
              <SectionVazia msg="Nenhum endereço cadastrado." />
            )}
            {e.contato?.emergencia && Object.values(e.contato.emergencia).some(v => v) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1">
                  <LifeBuoy className="w-3.5 h-3.5" /> Contato de emergência
                </div>
                <div className="text-sm text-gray-800">
                  {[e.contato.emergencia.nome, e.contato.emergencia.parentesco, e.contato.emergencia.telefone].filter(Boolean).join(' · ')}
                </div>
              </div>
            )}
          </section>

          {/* Família / dependentes */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" /> Família
            </h3>
            {e.familia && Object.values(e.familia).some(v => v !== null && v !== undefined && v !== '') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mb-3 pb-3 border-b border-gray-100">
                <Campo label="Estado civil" valor={e.familia.estado_civil} />
                <Campo label="Filhos" valor={e.familia.qtd_filhos != null ? String(e.familia.qtd_filhos) : undefined} />
                <Campo label="Cônjuge / companheiro(a)" valor={e.familia.conjuge_nome} />
                <Campo label="Telefone do cônjuge" valor={e.familia.conjuge_telefone} />
                <Campo label="Nascimento do cônjuge" valor={e.familia.conjuge_data_nascimento ? formatarData(e.familia.conjuge_data_nascimento) : undefined} />
                {e.familia.observacoes && (
                  <div className="md:col-span-2">
                    <Campo label="Observações" valor={e.familia.observacoes} />
                  </div>
                )}
              </div>
            )}
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Dependentes</div>
            {Array.isArray(e.dependentes) && e.dependentes.length > 0 ? (
              <ul className="space-y-2">
                {e.dependentes.map((dep, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm border-l-2 border-purple-200 pl-3 py-1">
                    <span className="font-medium text-gray-800">{dep.nome || '—'}</span>
                    {dep.parentesco && <span className="text-gray-500">· {dep.parentesco}</span>}
                    {dep.data_nascimento && <span className="text-gray-400">· {formatarData(dep.data_nascimento)}</span>}
                    {dep.ir && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">IR</span>}
                    {dep.plano_saude && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">Plano saúde</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <SectionVazia msg="Nenhum dependente cadastrado." />
            )}
          </section>

          {/* Uniforme */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Shirt className="w-4 h-4 text-gray-400" /> Uniforme
            </h3>
            {(() => {
              const uni = e.uniforme;
              const tam = uni?.tamanhos || {};
              const pecas = (Object.keys(LABEL_PECA_UNIFORME) as PecaUniforme[]).filter(p => tam[p]);
              const entregas = uni?.entregas || [];
              if (pecas.length === 0 && entregas.length === 0 && !uni?.observacoes && !e.tamanho_camisa) {
                return <SectionVazia msg="Nenhum dado de uniforme cadastrado." />;
              }
              return (
                <>
                  <div className="flex flex-wrap gap-2">
                    {pecas.length > 0
                      ? pecas.map(p => (
                          <span key={p} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {LABEL_PECA_UNIFORME[p]} <span className="font-bold text-gray-900">{tam[p]}</span>
                          </span>
                        ))
                      : e.tamanho_camisa && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            Camisa <span className="font-bold text-gray-900">{e.tamanho_camisa}</span>
                          </span>
                        )}
                  </div>
                  {entregas.length > 0 && (
                    <ul className="space-y-2 mt-4">
                      {entregas.map((en, i) => (
                        <li key={i} className="flex flex-wrap items-center gap-2 text-sm border-l-2 border-purple-200 pl-3 py-1">
                          <span className="font-medium text-gray-800">{en.item || '—'}</span>
                          {en.tamanho && <span className="text-gray-500">· tam. {en.tamanho}</span>}
                          {en.quantidade ? <span className="text-gray-400">· {en.quantidade}x</span> : null}
                          {en.data_entrega && <span className="text-gray-400">· {formatarData(en.data_entrega)}</span>}
                          {en.devolvido && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">Devolvido</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {uni?.observacoes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">{uni.observacoes}</div>
                  )}
                </>
              );
            })()}
          </section>

          {/* Formação / Idiomas / Certificações */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-gray-400" /> Formação acadêmica
              </h3>
              {Array.isArray(e.formacao) && e.formacao.length > 0 ? (
                <ul className="space-y-2">
                  {e.formacao.map((f, i) => (
                    <li key={i} className="text-sm border-l-2 border-purple-200 pl-3 py-1">
                      <div className="font-medium text-gray-800">
                        {f.curso || '—'}
                        {f.nivel && <span className="ml-2 text-xs text-purple-600">{LABEL_NIVEL_FORMACAO[f.nivel] || cap(f.nivel)}</span>}
                      </div>
                      <div className="text-xs text-gray-500">
                        {[f.instituicao, f.ano_conclusao, f.situacao ? (LABEL_SITUACAO[f.situacao] || cap(f.situacao)) : null].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <SectionVazia msg="Nenhuma formação cadastrada." />
              )}
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Languages className="w-4 h-4 text-gray-400" /> Idiomas
              </h3>
              {Array.isArray(e.idiomas) && e.idiomas.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(e.idiomas as Idioma[]).map((idi, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {idi.idioma || '—'}
                      {idi.nivel && <span className="text-gray-400">· {LABEL_NIVEL_IDIOMA[idi.nivel] || cap(idi.nivel)}</span>}
                    </span>
                  ))}
                </div>
              ) : typeof e.idiomas === 'string' && e.idiomas ? (
                <div className="text-sm text-gray-800">{e.idiomas}</div>
              ) : (
                <SectionVazia msg="Nenhum idioma cadastrado." />
              )}
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-gray-400" /> Certificações
              </h3>
              {Array.isArray(e.certificacoes) && e.certificacoes.length > 0 ? (
                <ul className="space-y-2">
                  {e.certificacoes.map((c, i) => (
                    <li key={i} className="text-sm border-l-2 border-purple-200 pl-3 py-1">
                      <div className="font-medium text-gray-800">{c.nome || '—'}</div>
                      <div className="text-xs text-gray-500">
                        {[c.emissor, c.data_emissao ? `emitido ${formatarData(c.data_emissao)}` : null, c.data_validade ? `válido até ${formatarData(c.data_validade)}` : null].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <SectionVazia msg="Nenhuma certificação cadastrada." />
              )}
            </div>
          </section>

          {/* Tags */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" /> Tags
            </h3>
            {Array.isArray(e.tags) && e.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {e.tags.map(t => (
                  <span key={t} className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                    {t}
                  </span>
                ))}
              </div>
            ) : (
              <SectionVazia msg="Nenhuma tag cadastrada." />
            )}
          </section>

          {/* Documentos pessoais (sensível) */}
          {podeVerSensiveis && (
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" /> Documentos pessoais
              </h3>
              {colab.documentos_pessoais && Object.keys(colab.documentos_pessoais).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <Campo label="CPF" valor={formatarCPF(colab.documentos_pessoais.cpf || colab.cpf)} />
                  <Campo label="RG" valor={colab.documentos_pessoais.rg || colab.rg} />
                  <Campo label="PIS / PASEP" valor={colab.documentos_pessoais.pis} />
                  <Campo label="Título de eleitor" valor={colab.documentos_pessoais.titulo_eleitor} />
                  <Campo label="Reservista" valor={colab.documentos_pessoais.reservista} />
                  <Campo label="CTPS" valor={colab.documentos_pessoais.ctps && [colab.documentos_pessoais.ctps.numero, colab.documentos_pessoais.ctps.serie, colab.documentos_pessoais.ctps.uf].filter(Boolean).join(' / ')} />
                  <Campo label="CNH" valor={colab.documentos_pessoais.cnh && [colab.documentos_pessoais.cnh.numero, colab.documentos_pessoais.cnh.categoria, colab.documentos_pessoais.cnh.validade ? `val. ${formatarData(colab.documentos_pessoais.cnh.validade)}` : null].filter(Boolean).join(' · ')} />
                </div>
              ) : (
                <SectionVazia msg="Nenhum documento pessoal cadastrado." />
              )}
            </section>
          )}

          {/* Histórico de cargos (sensível) */}
          {podeVerSensiveis && (
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-amber-600" /> Histórico de cargos
              </h3>
              {Array.isArray(colab.historico_cargos) && colab.historico_cargos.length > 0 ? (
                <ul className="space-y-3">
                  {colab.historico_cargos.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 border-l-2 border-purple-200 pl-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">{h.cargo || '—'}</div>
                        <div className="text-xs text-gray-500">
                          {[
                            (h.data_inicio || h.data_fim) ? `${formatarData(h.data_inicio)} – ${h.data_fim ? formatarData(h.data_fim) : 'atual'}` : null,
                            h.centro_custo,
                            h.motivo,
                          ].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      {h.salario != null && h.salario !== '' && (
                        <div className="text-sm font-semibold text-gray-700">
                          {typeof h.salario === 'number' ? BRL.format(h.salario) : `R$ ${h.salario}`}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <SectionVazia msg="Nenhum histórico de cargos cadastrado." />
              )}
            </section>
          )}

          {/* Dados sensíveis (LGPD) */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              Dados sensíveis (LGPD)
            </h3>
            {sensivelOculto ? (
              <p className="text-sm text-gray-400 italic">
                Você não tem permissão para visualizar CPF e salário. Solicite acesso "RH Total" se necessário.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <Campo label="CPF" valor={formatarCPF(colab.cpf)} />
                <Campo label="Salário base" valor={colab.salario ? `R$ ${colab.salario}` : undefined} />
              </div>
            )}
          </section>

          <p className="text-[11px] text-gray-300 text-center">
            Sincronizado em {colab.data_sincronizacao ? new Date(colab.data_sincronizacao).toLocaleString('pt-BR') : '—'}
          </p>
        </>
      )}

      {aba === 'ferias' && <AbaFerias user={user} funccodi={funccodi} mostrarValor={podeEditar || isOwn} />}
      {aba === 'onboarding' && <AbaOnboarding user={user} funccodi={funccodi} podeEditar={podeEditar} />}
      {aba === 'desempenho' && <AbaDesempenho user={user} funccodi={funccodi} />}
      {aba === 'pdi' && <PdiApp user={user} podeEditar={podeEditar} funccodi={funccodi} fixo />}
      {aba === 'disc' && <AbaDiscFicha user={user} funccodi={funccodi} podeEditar={podeEditar} />}
      {aba === 'ocorrencias' && <HistoricoDisciplinar user={user} funccodi={funccodi} podeEditar={podeEditar} nome={d.nome} />}
      {aba === 'holerite' && podeVerSensiveis && <AbaHolerite user={user} funccodi={funccodi} />}

      {modalAberto && (
        <ModalEditarFicha
          user={user}
          colaborador={colab}
          podeVerSensiveis={podeVerSensiveis}
          onClose={() => setModalAberto(false)}
          onSalvo={() => { setModalAberto(false); carregar(); }}
        />
      )}
    </div>
  );
};

export default FichaColaborador;
